import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const expandWidth = keyframes`
  from { width: 0; }
  to { width: 80px; }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  animation: ${fadeIn} 0.8s ease;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #2e7d32;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  display: inline-block;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #4CAF50, #ff9800);
    border-radius: 2px;
    animation: ${expandWidth} 1s ease;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const PressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PressCard = styled.article`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.08);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, #4CAF50, #2e7d32);
    transform: scaleX(0);
    transition: transform 0.4s ease;
    z-index: 3;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 20px 50px rgba(46, 125, 50, 0.2);
  }
`;

const Thumb = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
`;

const ThumbImg = styled.img<{ $loaded: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${p => p.$loaded ? 1 : 0};
  transition: opacity 400ms ease;
  transform: scale(1);
  transition: opacity 400ms ease, transform 600ms cubic-bezier(0.2, 0.7, 0.3, 1);

  ${PressCard}:hover & {
    transform: scale(1.04);
  }
`;

const ThumbFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  padding: 1.5rem;
  gap: 0.5rem;
`;

const FallbackIcon = styled.span`
  font-size: 2.5rem;
  line-height: 1;
`;

const FallbackText = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.85;
`;

const PressBody = styled.div`
  padding: 1.75rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const EyebrowRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`;

const Publication = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #2e7d32;
`;

const PressDate = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #ff9800;
  letter-spacing: 0.1em;
`;

const PressTitle = styled.h3`
  color: #2e7d32;
  font-size: 1.35rem;
  line-height: 1.4;
  margin: 0;
`;

const Excerpt = styled.p`
  color: #444;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
`;

const ReadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  margin-top: 0.5rem;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%);
  color: white;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: gap 0.3s ease, background 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    gap: 0.85rem;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 152, 0, 0.3);
  }
`;

type Article = {
  publication: string;
  date: string;
  title: string;
  excerpt: string;
  href: string;
};

const ARTICLES: Article[] = [
  {
    publication: 'The Nation',
    date: '2024',
    title: 'SRA seeks sustained commitment to end HIV/AIDS in Nigeria.',
    excerpt:
      'Safe-Revive Africa makes the case for continued, well-funded action against HIV/AIDS — and warns of the cost of letting attention slip.',
    href: 'https://thenationonlineng.net/sra-seeks-sustained-commitment-to-end-hiv-aids-in-nigeria-2/',
  },
  {
    publication: 'Community Standard',
    date: '2024',
    title:
      'Public health group advocates renewed commitment, sustained investment against tuberculosis scourge.',
    excerpt:
      "Coverage of SRA's call for renewed national investment in the fight against tuberculosis, with a focus on community-level testing and care.",
    href: 'https://communitystandard.com.ng/health/public-health-group-advocates-renewed-commitment-sustained-investment-against-tuberculosis-scourge/',
  },
];

const PressThumb: React.FC<{ href: string; publication: string }> = ({ href, publication }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = `https://api.microlink.io/?url=${encodeURIComponent(href)}&embed=image.url`;

  return (
    <Thumb>
      {!failed && (
        <ThumbImg
          src={src}
          alt={`${publication} thumbnail`}
          loading="lazy"
          $loaded={loaded}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
      {(!loaded || failed) && (
        <ThumbFallback aria-hidden="true">
          <FallbackIcon>📰</FallbackIcon>
          <FallbackText>{publication}</FallbackText>
        </ThumbFallback>
      )}
    </Thumb>
  );
};

const Press: React.FC = () => {
  return (
    <Section id="research">
      <Title>Press</Title>
      <Subtitle>What's been written about the work.</Subtitle>

      <PressGrid>
        {ARTICLES.map((a, i) => (
          <PressCard key={i}>
            <PressThumb href={a.href} publication={a.publication} />
            <PressBody>
              <EyebrowRow>
                <Publication>{a.publication}</Publication>
                <PressDate>{a.date}</PressDate>
              </EyebrowRow>
              <PressTitle>{a.title}</PressTitle>
              <Excerpt>{a.excerpt}</Excerpt>
              <ReadLink href={a.href} target="_blank" rel="noopener noreferrer">
                Read the article →
              </ReadLink>
            </PressBody>
          </PressCard>
        ))}
      </PressGrid>
    </Section>
  );
};

export default Press;
